import {
  SET_CURRENT_STEP,
  SET_ERRORS,
  SET_INCOMPLETE_CONFIGURATION,
  SET_REGISTRATION_FIELD,
  SET_WHITELABEL_LOGO,
  SET_WHITELABEL_STATUS,
  TOGGLE_TAX,
  UPDATE_TAX_RATE_PERCENTAGE,
  SET_COUNTRIES,
  UPDATE_COUNTRIES,
  SET_FORM_FIELD,
  SET_LOADING_STATE,
  SET_STRIPE_ACCOUNT_ERROR,
  SET_WHITELABEL_CUSTOM_DOMAIN_MESSAGE,
} from "./constants";
import Joi from "joi/lib/index.js";
import _ from "lodash";
import {axiosCatchHandler} from "../../sharedGlobal";
import {moduleNamespace as productManagerModuleNamespace} from "../productManager/constants";
import {moduleNamespace as addonManagerModuleNamespace} from "../addonManager/constants";

/**
 * Fetch the current state of the whitelabel setup, relevant to the setup wizard.
 */
function fetchWhitelabelStatus({commit}) {
  return axios.get('/merchant/whitelabel-status') // TODO: Consider module axios promises, generally
    .then(({ data }) => {
      const { current_step, ...whitelabelStatus } = data;
      commit(SET_WHITELABEL_STATUS, whitelabelStatus);
      commit(SET_CURRENT_STEP, current_step);
    })
    .catch(e => {
      if (e.response.status === 404) {
        commit(SET_STRIPE_ACCOUNT_ERROR, e.response.data);
        return;
      }
      Swal.fire({
        type: 'error',
        text: 'An unknown error occurred, please contact support@hikeseo.co',
      });
    });
}

function fetchWhitelabelCustomDomainMessage({commit}) {
  return axios.get('/merchant/whitelabel-custom-domain-message')
      .then(({ data }) => {
        commit(SET_WHITELABEL_CUSTOM_DOMAIN_MESSAGE, data.whitelabel_custom_domain_message);
        commit(SET_FORM_FIELD, { customDomain: data.whitelabel_custom_domain });
      })
      .catch(e => {
        Swal.fire({
          type: 'error',
          text: 'An unknown error occurred, please contact support@hikeseo.co',
        });
      });
}

/**
 * Move back one step in the wizard process.
 * TODO: Are we allowing back-peddling on the wizard?
 */
function previousStep({ commit, getters }) {
  const currentStepIndex = _.indexOf(getters.allSteps, getters.currentStep);
  const prevStepIndex = currentStepIndex - 1;
  const prevStep = getters.allSteps[prevStepIndex];
  if (prevStep) {
    commit(SET_CURRENT_STEP, prevStep);
  }
}

function setLoadingState({ commit }, boolean) {
  commit('SET_LOADING_STATE', boolean);
}

/**
 * Store all user-created products and addons to the database for persistence between sessions.
 * They will not be connected/sent to Stripe using this method, it is merely for incomplete wizard setups.
 */
export function storeProductsAndAddons({ rootState, getters }) {
  const productState = rootState[productManagerModuleNamespace];
  const addonsState = rootState[addonManagerModuleNamespace];

  let {softwareProduct, supportAddon, supportedCurrencies} = productState;
  let {addons} = addonsState;

  let enabledCurrencies = [];
  Object.entries(supportedCurrencies).forEach(([currencyCode, {enabled}]) => {
    if (enabled) {
      enabledCurrencies.push(currencyCode);
    }
  });

  let payload = {
    softwareProduct,
    addons,
    enabledCurrencies,
  };

  if (getters.enableSupportAddon) {
    payload = {...payload, supportAddon};
  }

  return axios.post('/merchant/store-products-and-addons', payload);
}

/**
 * Run Joi validation on the provided requestParams and SET_ERRORS accordingly
 */
function joiValidate(requestParams, commit, dispatch) {
  const taxConfigSchema = Joi.object({
    percentage: Joi.number()
      .required()
      .label('Tax rate percentage')
      .greater(0.09)
      .less(100),
    countries: Joi.array()
      .label('Countries')
      .min(1),
  });

  const {error: errors} = taxConfigSchema.validate(
    requestParams,
    {abortEarly: false},
  );

  if (errors) {
    dispatch('setErrors', {
      errors: errors.details,
      namespace: 'taxConfig',
      joi: true,
    })
      .then(() => {
        commit(SET_LOADING_STATE, false);
      });

    return false;
  }

  return true;
}

export default {
  fetchWhitelabelStatus,
  fetchWhitelabelCustomDomainMessage,
  previousStep,
  setLoadingState,
  storeProductsAndAddons,
  setCurrentStep: ({commit}, step) => commit(SET_CURRENT_STEP, step),
  setIncompleteConfiguration: ({commit, rootState}, payload) => commit(
    SET_INCOMPLETE_CONFIGURATION,
    {
      rootState,
      payload,
    }
  ),
  setRegistrationField: ({commit}, payload) => commit(SET_REGISTRATION_FIELD, payload),
  setWhitelabelLogo: ({commit}, payload) => commit(SET_WHITELABEL_LOGO, payload),
  setErrors: ({commit}, payload) => commit(SET_ERRORS, payload),
  toggleTax: ({commit}, payload) => commit(TOGGLE_TAX, payload),
  updateTaxRatePercentage: ({commit}, payload) => commit(UPDATE_TAX_RATE_PERCENTAGE, payload),
  setCountries: ({commit}, payload) => commit(SET_COUNTRIES, payload),
  updateCountries: ({commit}, payload) => commit(UPDATE_COUNTRIES, payload),
  storeTaxConfig: ({state, commit, dispatch}) => {
    if (!state.taxConfig.charging) {
      axios.post('/merchant/store-tax-config', {
          countries: [],
        })
        .then(() => {
          commit(SET_LOADING_STATE, false);
          state.currentStep = 'PRODUCTS';
        });
    }
    state.errors = {};

    commit(SET_LOADING_STATE, true);

    const {percentage, countries} = state.taxConfig;
    const requestParams = {
      percentage: parseFloat(percentage),
      countries,
    };

    if (joiValidate(requestParams, commit, dispatch)) {
      axios.post('/merchant/store-tax-config', requestParams)
        .then(() => {
          commit(SET_LOADING_STATE, false);
          Swal.fire({
            icon: 'info',
            html: `In the next two steps, you'll set up your pricing for the platform and add-ons.
              When determining your prices, make sure to exclude taxes as these will be added during checkout 
              when your customer signs up.`,
            confirmButtonText: 'Ok, I understand',
          }).then(() => {
            state.currentStep = 'PRODUCTS';
          });
        })
        .catch(
          (error) => axiosCatchHandler(error, ({response}) => {
            let {status, data} = response;

            if (status === 422) {
              dispatch('setErrors', {
                errors: data.errors,
                namespace: 'taxConfig',
              })
                .then(() => {
                  commit(SET_LOADING_STATE, false);
                });

              return;
            }

            Swal.fire({
              icon: 'error',
              html: 'Whoops, there was a problem saving your tax configuration<br>Please try again later.',
            });

            commit(SET_LOADING_STATE, false);
          })
        );
    }
  },
  setFormField: ({commit}, payload) => commit(SET_FORM_FIELD, payload),
};
